import { useTranslation } from 'react-i18next'
import { Controller, Control, Path } from 'react-hook-form'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { SmartRetryFormData } from '../page'
import { LabelWithTooltip } from '@/components/ui/label-with-tooltip'

interface AdvancedOptionsProps {
    control: Control<SmartRetryFormData>
}

type AdvancedOptionField = {
    name: 'expiredCardNoRetries' | 'retryOnPayrollDays' | 'avoidWeekends' | 'velocityLimitWait24h' | 'timeoutRetryMinutes' | 'code51RetryPayroll' | 'code05Wait48h'
    labelKey: string
    tooltipKey: string
}

const ADVANCED_OPTION_FIELDS: AdvancedOptionField[] = [
    {
        name: 'expiredCardNoRetries',
        labelKey: 'smartRetry.fields.advancedOptions.expiredCardNoRetries',
        tooltipKey: 'smartRetry.fields.advancedOptions.expiredCardNoRetriesTooltip',
    },
    {
        name: 'retryOnPayrollDays',
        labelKey: 'smartRetry.fields.advancedOptions.retryOnPayrollDays',
        tooltipKey: 'smartRetry.fields.advancedOptions.retryOnPayrollDaysTooltip',
    },
    {
        name: 'avoidWeekends',
        labelKey: 'smartRetry.fields.advancedOptions.avoidWeekends',
        tooltipKey: 'smartRetry.fields.advancedOptions.avoidWeekendsTooltip',
    },
    {
        name: 'velocityLimitWait24h',
        labelKey: 'smartRetry.fields.advancedOptions.velocityLimitWait24h',
        tooltipKey: 'smartRetry.fields.advancedOptions.velocityLimitWait24hTooltip',
    },
    {
        name: 'timeoutRetryMinutes',
        labelKey: 'smartRetry.fields.advancedOptions.timeoutRetryMinutes',
        tooltipKey: 'smartRetry.fields.advancedOptions.timeoutRetryMinutesTooltip',
    },
    {
        name: 'code51RetryPayroll',
        labelKey: 'smartRetry.fields.advancedOptions.code51RetryPayroll',
        tooltipKey: 'smartRetry.fields.advancedOptions.code51RetryPayrollTooltip',
    },
    {
        name: 'code05Wait48h',
        labelKey: 'smartRetry.fields.advancedOptions.code05Wait48h',
        tooltipKey: 'smartRetry.fields.advancedOptions.code05Wait48hTooltip',
    },
]

const renderSwitch = (value: boolean, onChange: (checked: boolean) => void) => (
    <Switch checked={value} onCheckedChange={onChange} />
)

const renderOptionField = (
    field: AdvancedOptionField,
    control: Control<SmartRetryFormData>,
    t: (key: string, options?: { defaultValue?: string }) => string
) => {
    const fieldPath = `config.advancedOptions.${field.name}` as Path<SmartRetryFormData>

    return (
        <div key={field.name} className="flex items-center justify-between">
            <LabelWithTooltip
                label={t(field.labelKey, { defaultValue: field.name })}
                tooltip={t(field.tooltipKey, { defaultValue: '' })}
            />
            <Controller
                name={fieldPath}
                control={control}
                render={({ field: { value, onChange } }) => {
                    if (typeof value !== 'boolean') {
                        return <Switch checked={false} onCheckedChange={() => { }} />
                    }
                    return renderSwitch(value, onChange)
                }}
            />
        </div>
    )
}

export const AdvancedOptions = ({ control }: AdvancedOptionsProps) => {
    const { t } = useTranslation('paymentFlows')

    return (
        <div>
            <Label className="text-sm font-medium mb-3 block">
                {t('smartRetry.fields.advancedOptions.label')}
            </Label>
            <div className="space-y-3">
                {ADVANCED_OPTION_FIELDS.map((field) => renderOptionField(field, control, t))}
            </div>
        </div>
    )
}

